import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatCents, type BillingOverview } from '@kontor/shared';
import { APP_VENDOR, APP_VERSION, MODULES, modulePlan, PLAN_LABELS, PLAN_PRICES_GROSS_MONTH_CENTS, PLANS, downgradeText, modulesForPlan, upgradeText, type Plan } from '@kontor/shared';
import { api, ApiError } from '../api';
import { DowngradeDialog } from './DowngradeDialog';
import { BillingSections } from './BillingPage';
import { SeatsCard } from '../components/SeatsCard';
import { InfoHint } from '../components/InfoHint';
import { PlanIcon } from '../components/PlanIcon';
import { planUrl } from '../links';
import { useMeta } from '../meta';
import { useSession } from '../session';
import './about.css';

/** Kurzbeschreibung ohne Modulnamen: Welche Module in einem Paket stecken, bestimmt der Betreiber und steht in der Liste darunter */
const PLAN_TEXT: Record<Plan, string> = {
  basic: 'Für den Einstieg und das Tagesgeschäft vom Kontakt bis zum Auftrag.',
  standard: 'Für den Alltag eines Betriebs mit Team, Vertrieb und Buchhaltung.',
  premium: 'Für wachsende Betriebe mit Personal, Fahrzeugen und Lager.',
};

/** Module, die als Eintrag vorhanden sind, aber noch nicht vollständig: Was heute schon geht, steht dabei */
const MODULE_NOTE: Record<string, string> = { api_access: ' (Verbindungen verfügbar, eigene API in Vorbereitung)' };

const dayDe = (iso: string) => iso.split('-').reverse().join('.');
const dateFormat = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

/**
 * Einrichtung → Wirkia Konto (Änderungswunsch vom 21.10.2026): der Platz, an dem der Mandant sein Wirkia-Paket verwaltet. Angaben zum Konto (Unternehmen, Mandantennummer, Paket,
 * Zusatzmodule, Lizenz), die Pakete mit Hoch- und Herunterstufen, die Plätze und Zusatzbenutzer (buchen und kündigen) sowie Abonnement, Zahlungen und Rechnungen. Löst „Über Wirkia“,
 * „Abonnement und Rechnungen“ und die Buchung der Zusatzbenutzer in der Benutzerverwaltung ab. Die Version des Servers kommt aus GET /meta. Weicht sie von der des geladenen
 * Backoffice ab (zum Beispiel nach einem Update), steht ein Hinweis zum Neuladen da.
 */
export function WirkiaAccountPage() {
  const { session, can } = useSession();
  const client = useQueryClient();
  const [downgradeTo, setDowngradeTo] = useState<Plan | null>(null);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  // Die Abrechnung sieht nur, wer das Recht dazu hat. Ohne sie führt „Herunterstufen“ zur Webseite.
  const billing = useQuery({ queryKey: ['billing'], queryFn: async () => (await api<{ data: BillingOverview }>('GET', '/billing')).data, enabled: can('setup.billing'), retry: false });
  const withdraw = useMutation({
    mutationFn: () => api('DELETE', '/billing/downgrade'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['billing'] });
      setNotice({ ok: true, text: 'Der Wechsel ist zurückgenommen. Ihr Paket, Ihre Daten und Ihr Preis bleiben unverändert.' });
    },
    onError: (e) => setNotice({ ok: false, text: e instanceof ApiError ? e.message : 'Das Zurücknehmen hat nicht geklappt.' }),
  });
  const meta = useMeta();
  if (!session) return null;

  const plan = (PLANS as readonly string[]).includes(session.tenant.plan) ? (session.tenant.plan as Plan) : 'basic';
  const included = new Set(modulesForPlan(plan));
  const extras = MODULES.filter((m) => session.tenant.modules.includes(m.key) && !included.has(m.key));
  const order = billing.data?.order ?? null;
  const scheduled = order?.downgrade ?? null;
  const serverVersion = meta.data?.version;
  const mismatch = !!serverVersion && serverVersion !== APP_VERSION;
  const vendor = Object.values(APP_VENDOR).some(Boolean);

  return (
    <div className="about">
      <div className="page-head">
        <h1>Wirkia Konto</h1>
      </div>
      <p className="muted">Hier verwalten Sie Ihr Wirkia-Paket: Paket wechseln, Zusatzbenutzer buchen und kündigen, Zahlungen und Rechnungen ansehen.</p>

      {mismatch && (
        <div className="alert alert--error" role="alert">
          Der Server läuft mit Version {serverVersion}, diese Seite mit Version {APP_VERSION}. Laden Sie die Seite neu (Strg+F5, auf dem Handy die Seite neu laden), damit beides zusammenpasst.
        </div>
      )}

      <section className="card">
        <h2>Ihr System</h2>
        <dl className="about__facts">
          <dt>Unternehmen</dt>
          <dd>{session.tenant.displayName}</dd>
          <dt>Mandantennummer</dt>
          <dd>{session.tenant.number}</dd>
          <dt>Paket</dt>
          <dd><PlanIcon plan={plan} size={18} /> {PLAN_LABELS[plan]}</dd>
          <dt>Gebuchte Zusatzmodule</dt>
          <dd>{extras.length > 0 ? extras.map((m) => m.label).join(', ') : 'keine'}</dd>
          <dt>Lizenz gültig bis</dt>
          <dd>{session.tenant.validUntil ? dateFormat.format(new Date(session.tenant.validUntil)) : 'unbefristet'}</dd>
        </dl>
      </section>

      <section className="card">
        <h2>Die Pakete</h2>
        {notice && <div className={`alert ${notice.ok ? 'alert--ok' : 'alert--error'}`} role={notice.ok ? 'status' : 'alert'}>{notice.text}</div>}
        {scheduled && (
          <div className="alert alert--warn about__scheduled" role="status">
            <p>
              <strong>Herunterstufen vorgemerkt:</strong> Ab dem {dayDe(scheduled.effectiveOn)} gilt das Paket {PLAN_LABELS[scheduled.plan]} für {formatCents(PLAN_PRICES_GROSS_MONTH_CENTS[scheduled.plan])} brutto je Benutzer und Monat. Dann werden die Daten der Bereiche gelöscht, die
              {' '}{PLAN_LABELS[scheduled.plan]} nicht enthält. Bis dahin gilt Ihr Paket unverändert.
            </p>
            {scheduled.providerApprovalUrl && (
              <p>
                PayPal verlangt Ihre Bestätigung für den neuen Preis: <a href={scheduled.providerApprovalUrl} target="_blank" rel="noopener noreferrer">Bei PayPal bestätigen<span className="sr-only"> (öffnet in neuem Tab)</span></a>.
              </p>
            )}
            <button type="button" className="btn btn--secondary btn--sm" disabled={withdraw.isPending} onClick={() => { setNotice(null); withdraw.mutate(); }}>
              {withdraw.isPending ? 'Wird zurückgenommen …' : 'Herunterstufen zurücknehmen'}
            </button>
          </div>
        )}
        <p className="muted small">Wirkia ist in Modulen aufgebaut. Das Paket bestimmt, welche Module Sie nutzen. Einzelne Module lassen sich dazubuchen. Gastronomie, Chauffeur-Service und Weinhandel sind keine Module, sondern eine Einstellung unter Firmendaten, die die vorhandenen Module an Ihren Alltag anpasst.</p>
        <p className="muted small">Die Preise gelten je Benutzer und Monat inklusive Umsatzsteuer und werden für zwölf Monate im Voraus abgerechnet. In jedem Paket ist ein Benutzer enthalten, jeder weitere kostet 5 € brutto im Monat. Die erste Einrichtung und Unterstützung durch Wirkia erfolgt nach individuellem Angebot, schreiben Sie uns an kontakt@wirkia.de.</p>
        <div className="about__plans">
          {PLANS.map((p) => {
            const mods = MODULES.filter((m) => modulePlan(m) === p);
            const current = p === plan;
            return (
              <article key={p} className={`about__plan${current ? ' is-current' : ''}`} aria-label={`Paket ${PLAN_LABELS[p]}`}>
                <header>
                  <PlanIcon plan={p} size={28} title />
                  <h3>{PLAN_LABELS[p]}</h3>
                  {current && <span className="about__badge">Ihr Paket</span>}
                </header>
                <p className="about__price"><strong>{formatCents(PLAN_PRICES_GROSS_MONTH_CENTS[p])}</strong> <span className="muted small">brutto je Benutzer und Monat</span></p>
                <p className="small">{PLAN_TEXT[p]}</p>
                <ul className="about__modules">
                  {mods.map((m) => (
                    <li key={m.key} className={m.available ? undefined : 'is-soon'} title={m.description}>
                      {m.label}{m.available ? '' : (MODULE_NOTE[m.key] ?? ' (in Vorbereitung)')}
                    </li>
                  ))}
                </ul>
                {p !== 'basic' && <p className="muted small">Enthält zusätzlich alles aus {p === 'standard' ? 'Basic' : 'Standard und Basic'}.</p>}
                {!current && (
                  <div className="about__change">
                    {PLANS.indexOf(p) > PLANS.indexOf(plan) ? (
                      <>
                        {/* In der Testphase bestellt man im Backoffice, sonst führt der Weg zum Bereich des Pakets auf der Webseite */}
                        {session.tenant.trial ? (
                          <Link className="btn btn--primary btn--sm" to={`/bestellen?paket=${p}`}>Buchen</Link>
                        ) : (
                          <a className="btn btn--primary btn--sm" href={planUrl(p)} target="_blank" rel="noopener noreferrer">Buchen<span className="sr-only"> (öffnet die Webseite in neuem Tab)</span></a>
                        )}
                        <InfoHint label={`Buchen von ${PLAN_LABELS[p]}`}>{upgradeText(plan, p, extras.map((m) => m.key))}</InfoHint>
                      </>
                    ) : (
                      <>
                        {billing.data?.canDowngrade ? (
                          <button type="button" className="btn btn--secondary btn--sm" onClick={() => { setNotice(null); setDowngradeTo(p); }}>
                            {scheduled?.plan === p ? 'Wechsel ändern' : 'Herunterstufen'}
                          </button>
                        ) : session.tenant.trial ? null : (
                          <a className="btn btn--secondary btn--sm" href={planUrl(p)} target="_blank" rel="noopener noreferrer">Herunterstufen<span className="sr-only"> (öffnet die Webseite in neuem Tab)</span></a>
                        )}
                        <InfoHint label={`Herunterstufen auf ${PLAN_LABELS[p]}`}>{downgradeText(plan, p, extras.map((m) => m.key))}</InfoHint>
                        {scheduled?.plan === p && <span className="about__badge about__badge--soon">Wechsel am {dayDe(scheduled.effectiveOn)}</span>}
                      </>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </section>

      {(can('users.read') || can('setup.billing')) && <SeatsCard />}

      {can('setup.billing') && <BillingSections />}

      {downgradeTo && (
        <DowngradeDialog
          plan={downgradeTo}
          onClose={() => setDowngradeTo(null)}
          onDone={(done) => {
            setDowngradeTo(null);
            setNotice({ ok: true, text: `Das Herunterstufen auf ${PLAN_LABELS[done.downgrade?.plan ?? downgradeTo]} ist vorgemerkt und gilt ab dem ${done.downgrade ? dayDe(done.downgrade.effectiveOn) : 'nächsten Abrechnungszeitraum'}. Sie können es bis dahin hier zurücknehmen.` });
          }}
        />
      )}

      {vendor && (
        <section className="card">
          <h2>Anbieter und Kontakt</h2>
          <dl className="about__facts">
            {APP_VENDOR.name && (<><dt>Anbieter</dt><dd>{APP_VENDOR.name}</dd></>)}
            {APP_VENDOR.address && (<><dt>Anschrift</dt><dd className="about__pre">{APP_VENDOR.address}</dd></>)}
            {APP_VENDOR.email && (<><dt>E-Mail</dt><dd><a href={`mailto:${APP_VENDOR.email}`}>{APP_VENDOR.email}</a></dd></>)}
            {APP_VENDOR.phone && (<><dt>Telefon</dt><dd>{APP_VENDOR.phone}</dd></>)}
            {APP_VENDOR.website && (<><dt>Webseite</dt><dd><a href={APP_VENDOR.website} target="_blank" rel="noreferrer">{APP_VENDOR.website}</a></dd></>)}
          </dl>
        </section>
      )}


      <p className="muted small about__footer">
        Wirkia Version {APP_VERSION}{serverVersion && serverVersion !== APP_VERSION ? `, Server ${serverVersion}` : ''}
      </p>
    </div>
  );
}
